import React, { useState, useMemo } from 'react'
import { useTranslation } from '../../i18n/LanguageContext'
import { useHours } from './useHours'
import type { Difficulty, GameMode } from './useHours'
import { ROUNDS, OPTION_COUNT } from './HoursData'
import {
  AnalogClock,
  DigitalClockDisplay,
  InteractiveAnalogClock,
  DigitalSpinner,
  SmallAnalogClock,
  SmallDigitalClock,
} from './ClockComponents'
import GamePlayHeader     from '../GamePlayHeader'
import GameCompleteScreen from '../GameCompleteScreen'
import GameSetupScreen    from '../GameSetupScreen'
import SaveErrorBanner    from '../SaveErrorBanner'
import { ss } from '../gameSetupStyles'
import { COLORS, FONT, RADIUS, SHADOWS } from '../../styles/tokens'
import { formatGameTime } from '../utils'
import { useGameCompletion } from '../../hooks/useGameCompletion'
import { useGameSession } from '../../hooks/useGameSession'
import { useSessionMode } from '../../professional/SessionModeContext'
import type { HoursTranslations } from '../../i18n/types'

type GameScreen = 'setup' | 'playing' | 'complete'

interface HoursGameProps {
  onBack:              () => void
  onGoToSubscription?: () => void
}

const DIFFICULTIES: Difficulty[] = ['easy', 'medium', 'hard']

export default function HoursGame({ onBack, onGoToSubscription }: HoursGameProps) {
  const { t } = useTranslation()
  const ht = t.hours
  const proSession = useSessionMode()

  const proModeStr = useMemo(() => (proSession?.config.mode as string) ?? '', [proSession])
  const [screen,     setScreen]     = useState<GameScreen>(proSession ? 'playing' : 'setup')
  const [mode,       setMode]       = useState<GameMode>(
    (proModeStr.replace('-reverse', '') as GameMode) || 'analog',
  )
  const [isReverse,  setIsReverse]  = useState(proModeStr.endsWith('-reverse'))
  const [difficulty, setDifficulty] = useState<Difficulty>(
    (proSession?.config.difficulty as Difficulty) ?? 'easy',
  )

  const hook = useHours(difficulty, mode, isReverse)
  const { state, resetGame } = hook

  const { gameComplete, moves, startTime, endTime, answered, questions } = state
  const sessionMode = `${mode}${isReverse ? '-reverse' : ''}`

  const { sessionId } = useGameSession({
    activitySlug: 'hours',
    difficulty,
    mode: sessionMode,
    enabled: screen === 'playing',
  })

  const { saveError, clearSaveError } = useGameCompletion({
    gameComplete,
    activitySlug:  'hours',
    difficulty,
    mode:          sessionMode,
    moves,
    startTime,
    endTime,
    pairsTotal:    questions.length,
    pairsMatched:  answered,
    sessionId,
    onCompleted:   () => setScreen('complete'),
  })

  const handleStart = () => { resetGame(); setScreen('playing') }

  return (
    <div style={s.root}>
      {saveError && (
        <SaveErrorBanner
          message={t.common.saveError}
          dismissLabel={t.common.dismissError}
          onDismiss={clearSaveError}
        />
      )}

      {screen === 'setup' && (
        <SetupScreen
          mode={mode}
          isReverse={isReverse}
          difficulty={difficulty}
          onModeChange={setMode}
          onReverseChange={setIsReverse}
          onDifficultyChange={setDifficulty}
          onStart={handleStart}
          onBack={onBack}
          onGoToSubscription={onGoToSubscription}
          ht={ht}
        />
      )}

      {screen === 'playing' && (
        <PlayScreen
          state={state}
          mode={mode}
          isReverse={isReverse}
          difficulty={difficulty}
          hook={hook}
          onBack={() => setScreen('setup')}
          ht={ht}
        />
      )}

      {screen === 'complete' && (
        <GameCompleteScreen
          emoji="🕐"
          title={ht.complete.title}
          subtitle={ht.complete.subtitle}
          stats={[
            { value: String(state.moves),                            label: ht.complete.movesLabel },
            { value: formatGameTime(state.startTime, state.endTime), label: ht.complete.timeLabel },
            { value: ht.difficulty[difficulty].label,                label: ht.complete.levelLabel },
          ]}
          playAgainLabel={ht.complete.playAgain}
          changeLevelLabel={ht.complete.changeLevel}
          backLabel={ht.complete.backToDashboard}
          onPlayAgain={() => { resetGame(); setScreen('playing') }}
          onChangeLevel={() => setScreen('setup')}
          onBack={onBack}
        />
      )}
    </div>
  )
}

// ── Setup Screen ──────────────────────────────────────────────────

function SetupScreen({ mode, isReverse, difficulty, onModeChange, onReverseChange, onDifficultyChange, onStart, onBack, onGoToSubscription, ht }: {
  mode:               GameMode
  isReverse:          boolean
  difficulty:         Difficulty
  onModeChange:       (m: GameMode) => void
  onReverseChange:    (r: boolean)  => void
  onDifficultyChange: (d: Difficulty) => void
  onStart:            () => void
  onBack:             () => void
  onGoToSubscription?: () => void
  ht:                 HoursTranslations
}) {
  const preview = (
    <div style={s.preview}>
      {!isReverse && mode === 'analog' && (
        <>
          <AnalogClock hour={3} minute={15} size={100} />
          <div style={s.previewLabel}>{ht.previewNormal}</div>
          <div style={s.previewOptions}>
            {['las tres y cuarto', 'las dos en punto'].map((opt, i) => (
              <div key={opt} style={{ ...s.previewOpt, ...(i === 0 ? s.previewOptCorrect : {}) }}>
                {opt}
              </div>
            ))}
          </div>
        </>
      )}
      {!isReverse && mode === 'digital' && (
        <>
          <DigitalClockDisplay hour={14} minute={30} />
          <div style={s.previewLabel}>{ht.previewNormal}</div>
          <div style={s.previewOptions}>
            {['las dos y media', 'las tres y cuarto'].map((opt, i) => (
              <div key={opt} style={{ ...s.previewOpt, ...(i === 0 ? s.previewOptCorrect : {}) }}>
                {opt}
              </div>
            ))}
          </div>
        </>
      )}
      {isReverse && (
        <>
          <div style={s.previewWritten}>{ht.previewReverseWritten}</div>
          <div style={s.previewLabel}>{ht.previewReverse}</div>
          <div style={s.previewClockRow}>
            {[{ h: 3, m: 15 }, { h: 6, m: 0 }].map(({ h, m }, i) => (
              <div key={i} style={{ ...s.previewClockOpt, ...(i === 0 ? s.previewOptCorrect : {}) }}>
                {mode === 'analog'
                  ? <SmallAnalogClock  hour={h} minute={m} size={64} />
                  : <SmallDigitalClock hour={h} minute={m} />
                }
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )

  const modeSelector = (
    <div className="fade-up" style={{ animationDelay: '0.1s' }}>
      <h2 style={ss.optionLabel}>{ht.chooseModeTitle}</h2>
      <div style={ss.modeGrid}>
        {(['analog', 'digital'] as GameMode[]).map((m) => (
          <button
            key={m}
            onClick={() => onModeChange(m)}
            style={{ ...ss.modeCard, ...(mode === m ? ss.modeCardActive : {}) }}
            aria-pressed={mode === m}
          >
            <span style={s.modeEmoji}>{m === 'analog' ? '🕐' : '🔢'}</span>
            <span style={s.modeLabel}>{ht.mode[m].label}</span>
            <span style={s.modeDesc}>{ht.mode[m].description}</span>
          </button>
        ))}
      </div>

      <div style={s.reverseRow}>
        <button
          onClick={() => onReverseChange(!isReverse)}
          style={{ ...s.reverseBtn, ...(isReverse ? s.reverseBtnActive : {}) }}
          aria-pressed={isReverse}
        >
          <span style={s.reverseIcon}>🔄</span>
          <div style={s.reverseBtnText}>
            <span style={s.reverseLabel}>{ht.reverseLabel}</span>
            <span style={s.reverseHint}>{ht.reverseHint}</span>
          </div>
          <span style={{ ...s.reverseCheck, background: isReverse ? COLORS.green : COLORS.borderWarm }}>
            {isReverse ? '✓' : ''}
          </span>
        </button>
      </div>
    </div>
  )

  return (
    <GameSetupScreen
      title={ht.title}
      subtitle={ht.subtitle}
      backLabel={ht.backToDashboard}
      onBack={onBack}
      onGoToSubscription={onGoToSubscription}
      difficulties={DIFFICULTIES.map((id) => ({
        id,
        label: ht.difficulty[id].label,
        hint:  ht.difficulty[id].hint
          .replace('{rounds}',  String(ROUNDS[id]))
          .replace('{options}', String(OPTION_COUNT[id])),
      }))}
      selectedDifficulty={difficulty}
      onDifficultyChange={(id) => onDifficultyChange(id as Difficulty)}
      chooseDifficultyLabel={ht.chooseDifficultyTitle}
      startLabel={ht.startBtn}
      onStart={onStart}
      preview={preview}
    >
      {modeSelector}
    </GameSetupScreen>
  )
}

// ── Play Screen ───────────────────────────────────────────────────

type HookReturn = ReturnType<typeof useHours>

function PlayScreen({ state, mode, isReverse, difficulty, hook, onBack, ht }: {
  state:      HookReturn['state']
  mode:       GameMode
  isReverse:  boolean
  difficulty: Difficulty
  hook:       HookReturn
  onBack:     () => void
  ht:         HoursTranslations
}) {
  const q = state.questions[state.currentIdx]
  if (!q) return null

  const progress  = (state.currentIdx / state.questions.length) * 100
  const currentNo = String(state.currentIdx + 1)
  const totalStr  = String(state.questions.length)

  return (
    <div style={s.playRoot}>
      <GamePlayHeader
        title={ht.title}
        stats={[
          { value: `${currentNo}/${totalStr}`, label: ht.question },
          { value: String(state.moves),        label: ht.moves },
        ]}
        progress={progress}
        progressNow={state.currentIdx}
        progressMax={state.questions.length}
        onBack={onBack}
        backLabel={ht.backToDashboard}
      />

      <div style={s.playContent}>
        {!isReverse && difficulty !== 'hard' && (
          <NormalMCQPlay   q={q} mode={mode} state={state} hook={hook} ht={ht} />
        )}
        {!isReverse && difficulty === 'hard' && (
          <NormalHardPlay  q={q} mode={mode} state={state} hook={hook} ht={ht} />
        )}
        {isReverse && difficulty !== 'hard' && (
          <ReverseMCQPlay  q={q} mode={mode} state={state} hook={hook} ht={ht} />
        )}
        {isReverse && difficulty === 'hard' && (
          <ReverseHardPlay q={q} mode={mode} state={state} hook={hook} ht={ht} />
        )}
      </div>
    </div>
  )
}

// ── Sub-play props ────────────────────────────────────────────────

type SubPlayProps = {
  q:     HookReturn['state']['questions'][0]
  mode:  GameMode
  state: HookReturn['state']
  hook:  HookReturn
  ht:    HoursTranslations
}

// ── Normal MCQ ────────────────────────────────────────────────────

function NormalMCQPlay({ q, mode, state, hook, ht }: SubPlayProps) {
  return (
    <>
      <p style={s.instruction}>{ht.instructionRead}</p>
      <div style={s.clockBox}>
        {mode === 'analog'
          ? <AnalogClock          hour={q.displayTime.hour} minute={q.displayTime.minute} size={180} />
          : <DigitalClockDisplay  hour={q.displayTime.hour} minute={q.displayTime.minute} />
        }
      </div>
      <div style={s.optionsGrid}>
        {q.writtenOptions.map((opt) => {
          const isFlash = state.correctFlashId === opt
          const isWrong = state.wrongId === opt
          return (
            <button
              key={opt}
              onClick={() => hook.selectWrittenOption(opt)}
              disabled={state.correctFlashId !== null}
              className={isWrong ? 'wm-shake' : undefined}
              style={{
                ...s.writtenOpt,
                ...(isFlash ? s.writtenOptCorrect : {}),
                ...(isWrong ? s.writtenOptWrong   : {}),
              }}
            >
              {opt}
            </button>
          )
        })}
      </div>
    </>
  )
}

// ── Normal Hard ───────────────────────────────────────────────────

function NormalHardPlay({ q, mode, state, hook, ht }: SubPlayProps) {
  const isWrong   = state.inputStatus === 'wrong'
  const isCorrect = state.inputStatus === 'correct'

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') hook.submitTextInput()
  }

  return (
    <>
      <p style={s.instruction}>{ht.instructionType}</p>
      <div style={s.clockBox}>
        {mode === 'analog'
          ? <AnalogClock         hour={q.displayTime.hour} minute={q.displayTime.minute} size={180} />
          : <DigitalClockDisplay hour={q.displayTime.hour} minute={q.displayTime.minute} />
        }
      </div>
      <div style={s.hardInputBlock}>
        <p style={s.exampleHint}>
          {ht.exampleHint}: <em>«{q.written}»</em>
        </p>
        <input
          type="text"
          value={state.inputValue}
          onChange={(e) => hook.setInputValue(e.target.value)}
          onKeyDown={handleKey}
          disabled={state.correctFlashId !== null}
          placeholder={ht.inputPlaceholder}
          className={isWrong ? 'wm-shake' : undefined}
          style={{
            ...s.textInput,
            ...(isCorrect ? s.textInputCorrect : {}),
            ...(isWrong   ? s.textInputWrong   : {}),
          }}
          aria-label={ht.inputPlaceholder}
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
        />
        <button
          onClick={hook.submitTextInput}
          disabled={!state.inputValue.trim() || state.correctFlashId !== null}
          style={{
            ...ss.startBtn,
            ...(!state.inputValue.trim() || state.correctFlashId !== null ? ss.startBtnDisabled : {}),
          }}
        >
          {ht.submitBtn}
        </button>
      </div>
    </>
  )
}

// ── Reverse MCQ ───────────────────────────────────────────────────

function ReverseMCQPlay({ q, mode, state, hook, ht }: SubPlayProps) {
  return (
    <>
      <p style={s.instruction}>{ht.instructionReverse}</p>
      <div style={s.writtenDisplay}>
        <span style={s.writtenDisplayText}>{q.written}</span>
      </div>
      <div style={s.clockGrid}>
        {q.clockOptions.map((opt) => {
          const isFlash = state.correctFlashId === opt.id
          const isWrong = state.wrongId === opt.id
          return (
            <button
              key={opt.id}
              onClick={() => hook.selectClockOption(opt.id)}
              disabled={state.correctFlashId !== null}
              className={isWrong ? 'wm-shake' : undefined}
              style={{
                ...s.clockOpt,
                ...(isFlash ? s.clockOptCorrect : {}),
                ...(isWrong ? s.clockOptWrong   : {}),
              }}
            >
              {mode === 'analog'
                ? <SmallAnalogClock  hour={opt.time.hour} minute={opt.time.minute} size={90} />
                : <SmallDigitalClock hour={opt.time.hour} minute={opt.time.minute} />
              }
            </button>
          )
        })}
      </div>
    </>
  )
}

// ── Reverse Hard ──────────────────────────────────────────────────

function ReverseHardPlay({ q, mode, state, hook, ht }: SubPlayProps) {
  const shake = state.interactiveSubmitWrong
  const flash = state.correctFlashId === '__interactive__'

  return (
    <>
      <p style={s.instruction}>{ht.instructionSet}</p>
      <div style={s.writtenDisplay}>
        <span style={s.writtenDisplayText}>{q.written}</span>
      </div>
      <div style={s.interactiveBox}>
        {mode === 'analog' ? (
          <>
            <p style={s.dragHint}>{ht.dragHint}</p>
            <InteractiveAnalogClock
              hour={state.interactiveHour}
              minute={state.interactiveMinute}
              size={210}
              onHourAngle={hook.dragHourToAngle}
              onMinuteAngle={hook.dragMinuteToAngle}
              shake={shake}
            />
          </>
        ) : (
          <>
            <p style={s.dragHint}>{ht.spinnerHint}</p>
            <DigitalSpinner
              hour={state.interactiveHour}
              minute={state.interactiveMinute}
              is24h={true}
              onStepHour={hook.stepHour}
              onStepMinute={hook.stepMinute}
              shake={shake}
            />
          </>
        )}
        <button
          onClick={hook.submitInteractive}
          disabled={state.correctFlashId !== null}
          style={{
            ...ss.startBtn,
            marginTop: 8,
            ...(flash ? { background: COLORS.greenLight } : {}),
            ...(state.correctFlashId !== null ? ss.startBtnDisabled : {}),
          }}
        >
          {flash ? '✓' : ht.submitBtn}
        </button>
      </div>
    </>
  )
}

// ── Styles ────────────────────────────────────────────────────────

const s: Record<string, React.CSSProperties> = {
  root: { minHeight: '100vh', background: COLORS.bgPage },

  // Setup
  modeEmoji: { fontSize: FONT.size.xl, lineHeight: 1 },
  modeLabel: { fontSize: FONT.size.md, fontWeight: 700, color: COLORS.textDark },
  modeDesc:  { fontSize: FONT.size.sm, color: COLORS.textSecondary, fontWeight: 500 },

  reverseRow: { marginTop: 14 },
  reverseBtn: {
    display:     'flex',
    alignItems:  'center',
    gap:         14,
    width:       '100%',
    padding:     '16px 20px',
    borderRadius: RADIUS.card,
    background:  COLORS.bgCard,
    border:      `2px solid ${COLORS.borderWarm}`,
    boxShadow:   SHADOWS.subtle,
    cursor:      'pointer',
    textAlign:   'left',
    transition:  'all 0.15s ease',
    minHeight:   56,
  },
  reverseBtnActive: {
    border:     `2.5px solid ${COLORS.green}`,
    background: COLORS.greenSurface,
    boxShadow:  SHADOWS.active,
  },
  reverseIcon:    { fontSize: FONT.size.xl, flexShrink: 0 },
  reverseBtnText: { display: 'flex', flexDirection: 'column', gap: 2, flex: 1 },
  reverseLabel:   { fontSize: FONT.size.md, fontWeight: 700, color: COLORS.textDark },
  reverseHint:    { fontSize: FONT.size.sm, color: COLORS.textSecondary },
  reverseCheck: {
    width:          24,
    height:         24,
    borderRadius:   '50%',
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    fontSize:       FONT.size.sm,
    fontWeight:     800,
    color:          'white',
    flexShrink:     0,
    transition:     'background 0.2s',
  },

  preview: {
    display:       'flex',
    flexDirection: 'column',
    alignItems:    'center',
    gap:           14,
    padding:       '20px',
    background:    COLORS.bgCard,
    borderRadius:  RADIUS.card,
    border:        `1.5px solid ${COLORS.borderWarm}`,
  },
  previewLabel:   { fontSize: FONT.size.sm, color: COLORS.textSecondary, fontWeight: 600 },
  previewOptions: { display: 'flex', flexDirection: 'column', gap: 8, width: '100%', maxWidth: 260 },
  previewOpt: {
    padding:      '10px 16px',
    borderRadius: RADIUS.sm,
    background:   COLORS.bgPage,
    border:       `1.5px solid ${COLORS.borderWarm}`,
    fontSize:     FONT.size.base,
    fontWeight:   600,
    color:        COLORS.textBody,
    textAlign:    'center',
  },
  previewOptCorrect: {
    background: COLORS.greenSurface,
    border:     `2px solid ${COLORS.green}`,
    color:      COLORS.green,
  },
  previewWritten: {
    fontSize:   FONT.size.xl,
    fontWeight: 700,
    color:      COLORS.textPrimary,
    textAlign:  'center',
  },
  previewClockRow: { display: 'flex', gap: 14 },
  previewClockOpt: {
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    padding:        8,
    borderRadius:   RADIUS.sm,
    border:         `1.5px solid ${COLORS.borderWarm}`,
  },

  // Play
  playRoot: { minHeight: '100vh', background: COLORS.bgPage },
  playContent: {
    paddingTop:    120,
    paddingBottom: 48,
    paddingLeft:   20,
    paddingRight:  20,
    maxWidth:      560,
    margin:        '0 auto',
    display:       'flex',
    flexDirection: 'column',
    alignItems:    'center',
    gap:           24,
  },

  instruction: {
    fontSize:  FONT.size.md,
    fontWeight: 600,
    color:     COLORS.textBody,
    textAlign: 'center',
    fontStyle: 'italic',
    margin:    0,
  },

  clockBox: {
    display:        'flex',
    justifyContent: 'center',
    alignItems:     'center',
    padding:        8,
  },

  optionsGrid: {
    display:             'grid',
    gridTemplateColumns: '1fr 1fr',
    gap:                 12,
    width:               '100%',
  },
  writtenOpt: {
    padding:      '18px 12px',
    borderRadius: RADIUS.card,
    background:   COLORS.bgCard,
    border:       `2px solid ${COLORS.borderWarm}`,
    boxShadow:    SHADOWS.subtle,
    fontSize:     FONT.size.md,
    fontWeight:   600,
    color:        COLORS.textDark,
    cursor:       'pointer',
    minHeight:    64,
    transition:   'all 0.12s ease',
    textAlign:    'center',
  },
  writtenOptCorrect: {
    background: COLORS.greenSurface,
    border:     `2.5px solid ${COLORS.greenLight}`,
    color:      COLORS.green,
  },
  writtenOptWrong: {
    background: '#FFF0F0',
    border:     `2.5px solid ${COLORS.errorText}`,
    color:      COLORS.errorText,
  },

  hardInputBlock: {
    display:       'flex',
    flexDirection: 'column',
    gap:           16,
    width:         '100%',
  },
  exampleHint: {
    fontSize:  FONT.size.sm,
    color:     COLORS.textSecondary,
    textAlign: 'center',
    margin:    0,
  },
  textInput: {
    width:        '100%',
    padding:      '16px 18px',
    fontSize:     FONT.size.lg,
    fontWeight:   600,
    color:        COLORS.textDark,
    border:       `2px solid ${COLORS.borderWarm}`,
    borderRadius: RADIUS.card,
    background:   COLORS.bgCard,
    outline:      'none',
    boxSizing:    'border-box' as const,
    transition:   'border-color 0.15s',
    minHeight:    56,
  },
  textInputCorrect: {
    border:     `2px solid ${COLORS.greenLight}`,
    background: COLORS.greenSurface,
  },
  textInputWrong: {
    border:     `2px solid ${COLORS.errorText}`,
    background: '#FFF0F0',
  },

  writtenDisplay: {
    padding:      '20px 28px',
    background:   COLORS.bgCard,
    borderRadius: RADIUS.card,
    border:       `2px solid ${COLORS.borderWarm}`,
    boxShadow:    SHADOWS.card,
    width:        '100%',
    textAlign:    'center',
  },
  writtenDisplayText: {
    fontSize:   FONT.size.xl,
    fontWeight: 700,
    color:      COLORS.textPrimary,
    lineHeight: 1.3,
  },

  clockGrid: {
    display:        'flex',
    flexWrap:       'wrap',
    gap:            14,
    justifyContent: 'center',
  },
  clockOpt: {
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    padding:        14,
    borderRadius:   RADIUS.card,
    background:     COLORS.bgCard,
    border:         `2px solid ${COLORS.borderWarm}`,
    boxShadow:      SHADOWS.subtle,
    cursor:         'pointer',
    minHeight:      64,
    minWidth:       64,
    transition:     'all 0.12s ease',
  },
  clockOptCorrect: {
    background: COLORS.greenSurface,
    border:     `2.5px solid ${COLORS.greenLight}`,
  },
  clockOptWrong: {
    background: '#FFF0F0',
    border:     `2.5px solid ${COLORS.errorText}`,
  },

  interactiveBox: {
    display:        'flex',
    flexDirection:  'column',
    alignItems:     'center',
    gap:            20,
    width:          '100%',
  },
  dragHint: {
    fontSize:  FONT.size.sm,
    color:     COLORS.textSecondary,
    textAlign: 'center',
    margin:    0,
  },
}
